/**
 * RaceResultModal — v16.3 §1.2 + P2 (Alberto): formulario opcional del
 * día de carrera con 6 campos.
 *
 * Se abre desde:
 *   - PlanPage.RaceDayCard (B.3) cuando el atleta pulsa el botón
 *     "Reportar resultado" antes de que el plan transicione a recovery.
 *   - RacesPage.RaceCard (B.4) cuando el atleta entra a /carreras y la
 *     carrera está completed=false con race_date <= today.
 *
 * Campos opcionales (atleta puede dejar 0-6 rellenos):
 *   - Tiempo final (parser h:mm:ss o mm:ss).
 *   - Distancia final (km, decimales).
 *   - FC media + FC máxima (40-250 ppm).
 *   - Sensaciones 1-5 (segmented buttons, mismo patrón que perceived_effort
 *     del modal post-sesión v14_2 §8).
 *   - Clima (5 opciones: Calor / Frío / Viento / Lluvia / Normal).
 *
 * Si el atleta envía AL MENOS UN campo, el backend marca `completed=true`
 * automático (B.1). Envío con form vacío: NO se llama al endpoint, solo
 * onClose (defensa client-side adicional).
 *
 * Patrón inspirado en AddRaceModal: dialog role, backdrop, state local,
 * try/catch con ApiError mapping, botones cancel/submit.
 */
import { useState } from "react";

import { submitRaceResult } from "@/api/races";
import { t } from "@/i18n/es";
import { ApiError, type RaceResultPayload, type RaceTarget, type Weather } from "@/types/api";

const WEATHER_OPTIONS: ReadonlyArray<{
  value: Weather;
  labelKey: keyof typeof t.races;
}> = [
  { value: "calor", labelKey: "resultWeatherCalor" },
  { value: "frio", labelKey: "resultWeatherFrio" },
  { value: "viento", labelKey: "resultWeatherViento" },
  { value: "lluvia", labelKey: "resultWeatherLluvia" },
  { value: "normal", labelKey: "resultWeatherNormal" },
];

/** Parser permisivo: acepta `h:mm:ss` o `mm:ss` y devuelve segundos.
 *  Devuelve null si el formato no encaja. Patrón extiende `_parsePaceMmSs`
 *  de SessionDetailPage pero con soporte para hora opcional. */
function _parseTimeToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  // h:mm:ss
  const m1 = /^(\d{1,3}):([0-5]\d):([0-5]\d)$/.exec(trimmed);
  if (m1) {
    return Number(m1[1]) * 3600 + Number(m1[2]) * 60 + Number(m1[3]);
  }
  // mm:ss
  const m2 = /^(\d{1,3}):([0-5]\d)$/.exec(trimmed);
  if (m2) {
    return Number(m2[1]) * 60 + Number(m2[2]);
  }
  return null;
}

const HR_MIN = 40;
const HR_MAX = 250;

interface Props {
  raceId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Callback al éxito del PATCH. El padre suele cerrar el modal y
   *  refrescar la lista (toast de éxito gestionado en RacesPage). */
  onSuccess: (race: RaceTarget) => void;
}

export function RaceResultModal({ raceId, isOpen, onClose, onSuccess }: Props) {
  // Campos del form como strings (input controlado) + clima/feeling como
  // valores tipados directamente.
  const [timeStr, setTimeStr] = useState("");
  const [distanceStr, setDistanceStr] = useState("");
  const [avgHrStr, setAvgHrStr] = useState("");
  const [maxHrStr, setMaxHrStr] = useState("");
  const [feeling, setFeeling] = useState<number | null>(null);
  const [weather, setWeather] = useState<Weather | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const payload: RaceResultPayload = {};

    // Tiempo (parser h:mm:ss / mm:ss).
    if (timeStr.trim() !== "") {
      const secs = _parseTimeToSeconds(timeStr);
      if (secs === null || secs <= 0) {
        setError(t.races.resultErrorTimeInvalid);
        return;
      }
      payload.actual_time_seconds = secs;
    }

    // Distancia (float positivo).
    if (distanceStr.trim() !== "") {
      const km = Number(distanceStr.trim().replace(",", "."));
      if (!Number.isFinite(km) || km <= 0) {
        setError(t.races.resultErrorDistanceInvalid);
        return;
      }
      payload.actual_distance_km = km;
    }

    // HR (int 40-250).
    for (const [raw, key] of [
      [avgHrStr, "actual_avg_hr"] as const,
      [maxHrStr, "actual_max_hr"] as const,
    ]) {
      const trimmed = raw.trim();
      if (trimmed === "") continue;
      const n = Number(trimmed);
      if (!Number.isInteger(n) || n < HR_MIN || n > HR_MAX) {
        setError(t.races.resultErrorHrOutOfRange);
        return;
      }
      payload[key] = n;
    }

    // Sensaciones (1-5, defensa).
    if (feeling !== null) {
      if (!Number.isInteger(feeling) || feeling < 1 || feeling > 5) {
        setError(t.races.resultErrorFeelingInvalid);
        return;
      }
      payload.actual_feeling = feeling;
    }

    // Clima (enum, defensa).
    if (weather !== null) {
      payload.actual_weather = weather;
    }

    // Form completamente vacío: NO llamar al backend (no marcaría
    // completed=true sin datos reales). Solo cerrar.
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      const updated = await submitRaceResult(raceId, payload);
      onSuccess(updated);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // Mapear errors específicos del backend si los hay; fallback genérico.
        setError(t.races.resultErrorGeneric);
      } else {
        setError(t.races.resultErrorGeneric);
      }
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  }

  return (
    <div
      data-testid="race-result-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="race-result-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-carbon-raised p-5 max-h-[90vh] overflow-y-auto">
        <h2
          id="race-result-title"
          className="text-lg font-medium text-bone"
        >
          {t.races.resultModalTitle}
        </h2>
        <p className="mt-1 text-xs text-bone-muted">
          {t.races.resultModalSubtitle}
        </p>

        <form
          data-testid="race-result-form"
          onSubmit={onSubmit}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          {/* Tiempo final */}
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-gray-mid">
              {t.races.resultTimeLabel}
            </span>
            <input
              data-testid="race-result-time"
              type="text"
              inputMode="text"
              placeholder={t.races.resultTimePlaceholder}
              value={timeStr}
              onChange={(e) => setTimeStr(e.target.value)}
              className="rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
            />
            <span className="text-[11px] text-bone-muted">
              {t.races.resultTimeHint}
            </span>
          </label>

          {/* Distancia */}
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-gray-mid">
              {t.races.resultDistanceLabel}
            </span>
            <input
              data-testid="race-result-distance"
              type="text"
              inputMode="decimal"
              placeholder={t.races.resultDistancePlaceholder}
              value={distanceStr}
              onChange={(e) => setDistanceStr(e.target.value)}
              className="rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
            />
          </label>

          {/* FC media + máxima en grid 2 cols */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-widest text-gray-mid">
                {t.races.resultAvgHrLabel}
              </span>
              <input
                data-testid="race-result-avg-hr"
                type="number"
                inputMode="numeric"
                min={HR_MIN}
                max={HR_MAX}
                value={avgHrStr}
                onChange={(e) => setAvgHrStr(e.target.value)}
                className="rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-widest text-gray-mid">
                {t.races.resultMaxHrLabel}
              </span>
              <input
                data-testid="race-result-max-hr"
                type="number"
                inputMode="numeric"
                min={HR_MIN}
                max={HR_MAX}
                value={maxHrStr}
                onChange={(e) => setMaxHrStr(e.target.value)}
                className="rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
              />
            </label>
          </div>

          {/* Sensaciones 1-5 (segmented buttons, patrón ps-effort) */}
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-gray-mid">
              {t.races.resultFeelingLabel}
            </span>
            <div
              className="flex gap-2"
              role="group"
              aria-label={t.races.resultFeelingLabel}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`race-result-feeling-${n}`}
                  aria-pressed={feeling === n}
                  onClick={() => setFeeling(feeling === n ? null : n)}
                  className={`flex-1 rounded-md border px-0 py-2 text-sm transition-colors duration-fast ${
                    feeling === n
                      ? "border-lime bg-lime/15 text-lime"
                      : "border-border-subtle text-bone-muted hover:border-border-strong"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-bone-muted">
              {t.races.resultFeelingHint}
            </span>
          </div>

          {/* Clima (radio group inline con wrap) */}
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs uppercase tracking-widest text-gray-mid">
              {t.races.resultWeatherLabel}
            </legend>
            <div
              className="flex flex-wrap gap-2"
              data-testid="race-result-weather"
            >
              {WEATHER_OPTIONS.map((opt) => {
                const selected = weather === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                      selected
                        ? "border-lime bg-lime/10 text-bone"
                        : "border-border-subtle text-bone-muted hover:border-border-strong"
                    }`}
                  >
                    <input
                      type="radio"
                      name="race-result-weather"
                      value={opt.value}
                      checked={selected}
                      onChange={() =>
                        setWeather(weather === opt.value ? null : opt.value)
                      }
                      className="sr-only"
                      data-testid={`race-result-weather-${opt.value}`}
                    />
                    {t.races[opt.labelKey]}
                  </label>
                );
              })}
            </div>
          </fieldset>

          {error && (
            <p
              data-testid="race-result-error"
              role="alert"
              className="text-sm text-danger"
            >
              {error}
            </p>
          )}

          <div className="mt-1 flex items-center justify-end gap-3">
            <button
              type="button"
              data-testid="race-result-cancel"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.races.resultCloseCta}
            </button>
            <button
              type="submit"
              data-testid="race-result-submit"
              disabled={submitting}
              className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.races.resultSubmitCta}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
